import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import './StudioPage.css';
import { API_CONFIG } from '../../../config/api';
import { scrollDocumentToTop } from '../../../lib/scrollDocumentToTop';
import AppBarBottom from '../../apps/components/AppBarBottom';

/* Inline SVG icons (replacing lucide-react) */
const IconFilm = ({ className = '', size = 24 }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
    <line x1="7" y1="2" x2="7" y2="22" />
    <line x1="17" y1="2" x2="17" y2="22" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <line x1="2" y1="7" x2="7" y2="7" />
    <line x1="2" y1="17" x2="7" y2="17" />
    <line x1="17" y1="17" x2="22" y2="17" />
    <line x1="17" y1="7" x2="22" y2="7" />
  </svg>
);

const IconChevronRight = ({ className = '', size = 24 }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const IconSparkles = ({ className = '', size = 24 }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
    <path d="M5 16l1 3 3 1-1 3-3 1-1-3-3-1 1-3 3-1z" />
    <path d="M19 16l1 3 3 1-1 3-3 1-1-3-3-1 1-3 3-1z" />
  </svg>
);

const IconCheck = ({ className = '', size = 24 }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconPlay = ({ className = '', size = 24 }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const IconCamera = ({ className = '', size = 24 }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const SUBJECTS = [
  { id: 'physics', name: 'Physics' },
  { id: 'biology', name: 'Biology' },
  { id: 'chemistry', name: 'Chemistry' },
  { id: 'engineering', name: 'Engineering' },
  { id: 'economics', name: 'Economics' },
  { id: 'environment', name: 'Environmental' },
];

const DIFFICULTIES = [
  { id: 'a-level', name: 'A-Level', desc: 'Ages 16-18' },
  { id: 'undergraduate', name: 'Undergraduate', desc: 'University Year 1-2' },
  { id: 'advanced', name: 'Advanced', desc: 'Year 3+ / Professional' },
];

const MILESTONES = [
  { num: 1, label: 'Vision', Icon: IconSparkles },
  { num: 2, label: 'Generate', Icon: IconFilm },
  { num: 3, label: 'Ready', Icon: IconCheck },
];

// StudioPage: cinematic three-act case + branching questions + graphs
const StudioPage = () => {
  const [phase, setPhase] = useState('intro');
  const [step, setStep] = useState(1);
  const [gameData, setGameData] = useState({
    vision: '',
    subject: '',
    difficulty: '',
  });
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generated, setGenerated] = useState(false);
  const [typingText, setTypingText] = useState('');
  const [acts, setActs] = useState([]);
  const [summary, setSummary] = useState('');
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState([]);
  const [knowledgeGraph, setKnowledgeGraph] = useState({ nodes: [], edges: [] });
  const [reasoningGraph, setReasoningGraph] = useState({ nodes: [], edges: [] });
  const mainRef = useRef(null);

  // Continue sits at the bottom of a scrolling panel. The next step should
  // open at the top of that panel, not where the button was.
  useLayoutEffect(() => {
    scrollDocumentToTop();
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [phase, step]);

  useEffect(() => {
    if (phase === 'intro') {
      const text = 'IVORI STUDIO';
      let index = 0;
      const timer = setInterval(() => {
        if (index <= text.length) {
          setTypingText(text.substring(0, index));
          index++;
        } else {
          setTimeout(() => setPhase('studio'), 1500);
          clearInterval(timer);
        }
      }, 100);
      return () => clearInterval(timer);
    }
  }, [phase]);

  const handleGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    setProgress(0);
    setError('');

    try {
      const url = `${API_CONFIG.baseURL}/api/game-studio/generate`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vision: gameData.vision,
          subject: gameData.subject,
          difficulty: gameData.difficulty,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to generate experience');
      }

      const payload = await response.json();
      const narrative = payload?.narrative || {};
      console.log('[STUDIO] Narrative from backend', narrative);
      let actsFromApi = Array.isArray(narrative.acts) ? narrative.acts : [];

      setSummary(narrative.summary || '');
      // Narrative-only step: clear graphs/questions for now.
      setQuestions([]);
      setKnowledgeGraph({ nodes: [], edges: [] });
      setReasoningGraph({ nodes: [], edges: [] });
      // Normalize acts and ensure we always have 3 editable acts.
      if (!actsFromApi.length) {
        const baseSummary = narrative.summary || gameData.vision || '';
        actsFromApi = [
          {
            id: 'act_1',
            title: 'Act 1 – Setup',
            body: baseSummary,
          },
          {
            id: 'act_2',
            title: 'Act 2 – Ambiguity & Pressure',
            body:
              'Develop a second act where external pressure mounts around this case. Raise the stakes with quotes from stakeholders and conflicting priorities.',
          },
          {
            id: 'act_3',
            title: 'Act 3 – Freeze‑Frame Dilemma',
            body:
              'End on a time‑critical dilemma that forces a difficult decision. List 3–4 technical options in the format: Do X. Gain Y. Risk Z.',
          },
        ];
      }

      setActs(
        actsFromApi.map((a, idx) => ({
          id: a.id || `act_${idx + 1}`,
          title: a.title || `Act ${idx + 1}`,
          body: a.body || '',
        }))
      );

      // Second-stage call: generate KG, RG, and branching questions from the narrative.
      try {
        const graphsUrl = `${API_CONFIG.baseURL}/api/game-studio/graphs`;
        const graphsResp = await fetch(graphsUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            vision: gameData.vision,
            subject: gameData.subject,
            difficulty: gameData.difficulty,
            narrative: {
              summary: narrative.summary || '',
              acts: actsFromApi,
            },
          }),
        });

        if (graphsResp.ok) {
          const graphsPayload = await graphsResp.json();
          // Normalise questions into the shape expected by the UI:
          // { id, title, after_scene, text, options: [{ id, text, leads_to }] }
          const rawQuestions = Array.isArray(graphsPayload.questions) ? graphsPayload.questions : [];
          const normalisedQuestions = rawQuestions.map((q, idx) => ({
            id: q.question_id || q.id || `Q${idx + 1}`,
            title: q.title || '',
            after_scene: q.anchor_scene_id || q.after_scene || '',
            text: q.prompt || q.text || '',
            options: Array.isArray(q.options)
              ? q.options.map((opt, oIdx) => ({
                  id: opt.option_id || opt.id || (oIdx === 0 ? 'A' : 'B'),
                  text: opt.label || opt.text || '',
                  leads_to: opt.next_scene_id || opt.leads_to || '',
                }))
              : [],
          }));
          setQuestions(normalisedQuestions);

          const kg = graphsPayload.knowledge_graph;
          setKnowledgeGraph(
            kg && typeof kg === 'object'
              ? {
                  nodes: Array.isArray(kg.nodes) ? kg.nodes : [],
                  edges: Array.isArray(kg.edges) ? kg.edges : [],
                }
              : { nodes: [], edges: [] }
          );
          const rg = graphsPayload.reasoning_graph;
          setReasoningGraph(
            rg && typeof rg === 'object'
              ? {
                  nodes: Array.isArray(rg.nodes) ? rg.nodes : [],
                  edges: Array.isArray(rg.edges) ? rg.edges : [],
                }
              : { nodes: [], edges: [] }
          );
        } else {
          // Non-fatal: narrative still generated.
          // eslint-disable-next-line no-console
          console.warn('[STUDIO] Graph generation failed', await graphsResp.text());
        }
      } catch (graphErr) {
        // eslint-disable-next-line no-console
        console.error('[STUDIO] Graph generation error', graphErr);
      }

      // Simulate progress bar for nice UX
      const stages = [
        { time: 800, progress: 35 },
        { time: 900, progress: 65 },
        { time: 800, progress: 100 },
      ];
      for (const stage of stages) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, stage.time));
        setProgress(stage.progress);
      }

      setGenerated(true);
      setStep(2);
    } catch (e) {
      setError('We could not generate the experience. Please try again in a moment.');
      console.error('Studio generate error', e);
    } finally {
      setGenerating(false);
    }
  };

  if (phase === 'intro') {
    return (
      <div className="cinematic-intro">
        <div className="cinematic-grid-bg" />
        <div className="cinematic-intro-content">
          <IconFilm className="cinematic-intro-icon cinematic-animate-pulse" size={128} />
          <h1 className="cinematic-intro-title">
            {typingText}
            <span className="cinematic-animate-pulse">|</span>
          </h1>
        </div>
      </div>
    );
  }

  return (
    <div className="cinematic-page">
      <div className="cinematic-grid-bg cinematic-grid-bg-sm" />

      <header className="cinematic-header">
        <div className="cinematic-header-inner">
          <div className="cinematic-header-top">
            <IconFilm className="cinematic-header-icon" size={24} />
            <h1 className="cinematic-header-title">ivori Studio</h1>
          </div>

          <div className="cinematic-milestones">
            {MILESTONES.map((m, idx) => (
              <div key={m.num} className="cinematic-milestone">
                <div className="cinematic-milestone-inner">
                  <div
                    className={`cinematic-milestone-dot ${
                      step >= m.num
                        ? 'cinematic-milestone-dot--done'
                        : step === m.num - 1
                        ? 'cinematic-milestone-dot--current'
                        : 'cinematic-milestone-dot--pending'
                    }`}
                  >
                    <m.Icon className="cinematic-milestone-icon" size={20} />
                  </div>
                  <span
                    className={`cinematic-milestone-label ${
                      step >= m.num ? 'cinematic-milestone-label--active' : 'cinematic-milestone-label--inactive'
                    }`}
                  >
                    {m.label}
                  </span>
                </div>
                {idx < MILESTONES.length - 1 && (
                  <div
                    className={`cinematic-milestone-connector ${
                      step > m.num ? 'cinematic-milestone-connector--done' : 'cinematic-milestone-connector--pending'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </header>

      <main ref={mainRef} className="cinematic-main">
        <div className="cinematic-main-inner">
          <div className="cinematic-content">
            {step === 1 && (
              <div className="cinematic-step1 cinematic-animate-fadeIn">
                <div className="cinematic-section">
                  <h2 className="cinematic-section-title">Your Vision</h2>
                  <p className="cinematic-section-desc">
                    What topics should students master and how will they demonstrate understanding?
                  </p>
                  <textarea
                    className="cinematic-textarea"
                    value={gameData.vision}
                    onChange={(e) => setGameData({ ...gameData, vision: e.target.value })}
                    placeholder="Example: Students will analyze renewable energy trade-offs by comparing solar, wind, and hybrid systems. They'll calculate cost per kWh, evaluate environmental impact, and justify their choice considering budget constraints, reliability requirements, and community needs..."
                    rows={5}
                    autoFocus
                  />
                </div>

                <div className="cinematic-section">
                  <h3 className="cinematic-subsection-title">Subject Area</h3>
                  <div className="cinematic-grid-2">
                    {SUBJECTS.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`cinematic-pill ${gameData.subject === item.id ? 'cinematic-pill--active' : ''}`}
                        onClick={() => setGameData({ ...gameData, subject: item.id })}
                      >
                        {item.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="cinematic-section">
                  <h3 className="cinematic-subsection-title">Difficulty Level</h3>
                  <div className="cinematic-grid-3">
                    {DIFFICULTIES.map((level) => (
                      <button
                        key={level.id}
                        type="button"
                        className={`cinematic-pill ${gameData.difficulty === level.id ? 'cinematic-pill--active' : ''}`}
                        onClick={() => setGameData({ ...gameData, difficulty: level.id })}
                      >
                        <div>{level.name}</div>
                        <div className="cinematic-pill-desc">{level.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ textAlign: 'center', paddingTop: 24 }}>
                  <button
                    type="button"
                    className="cinematic-continue-btn"
                    onClick={() => setStep(2)}
                    disabled={
                      !gameData.vision ||
                      !gameData.subject ||
                      !gameData.difficulty ||
                      gameData.vision.length < 100
                    }
                  >
                    Continue
                    <IconChevronRight className="cinematic-chevron" size={24} />
                  </button>
                </div>
              </div>
            )}

            {step === 2 && !generating && !generated && (
              <div className="cinematic-step1 cinematic-animate-fadeIn">
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                  <h2 className="cinematic-section-title">Ready to Generate</h2>
                  <p className="cinematic-section-desc">Review your vision before generating</p>
                </div>

                {error && (
                  <p style={{ color: 'var(--iv-danger)', marginBottom: 16, textAlign: 'center', fontSize: '0.9rem' }}>
                    {error}
                  </p>
                )}

                <div className="cinematic-review-box cinematic-section">
                  <div className="cinematic-review-label">Vision</div>
                  <div className="cinematic-review-text">{gameData.vision}</div>
                  <div className="cinematic-review-grid">
                    <div>
                      <div className="cinematic-review-label">Subject</div>
                      <div className="cinematic-review-text">
                        {SUBJECTS.find((s) => s.id === gameData.subject)?.name}
                      </div>
                    </div>
                    <div>
                      <div className="cinematic-review-label">Level</div>
                      <div className="cinematic-review-text">
                        {DIFFICULTIES.find((d) => d.id === gameData.difficulty)?.name}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="cinematic-orange-box cinematic-section">
                  <h4>What gets generated:</h4>
                  <ul>
                    <li>• 5 cinematic scenes with professional cinematography</li>
                    <li>• 2-3 deep critical thinking questions requiring calculations</li>
                    <li>• Branching narrative based on student decisions</li>
                    <li>• 15-20 minute gameplay experience</li>
                  </ul>
                </div>

                <div className="cinematic-actions-row">
                  <button type="button" className="cinematic-btn-back" onClick={() => setStep(1)}>
                    Back
                  </button>
                  <button
                    type="button"
                    className="cinematic-btn-generate"
                    onClick={handleGenerate}
                  >
                    <IconSparkles className="cinematic-sparkles" size={24} />
                    Generate Experience
                  </button>
                </div>
              </div>
            )}

            {step === 2 && generating && (
              <div className="cinematic-generating cinematic-animate-fadeIn">
                <div className="cinematic-generating-inner">
                  <IconFilm className="cinematic-generating-icon cinematic-animate-spin-slow" size={128} />
                  <div className="cinematic-generating-percent">{progress}%</div>
                  <div className="cinematic-progress-track">
                    <div
                      className="cinematic-progress-fill"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="cinematic-generating-label cinematic-animate-pulse">
                    CREATING EXPERIENCE
                  </div>
                </div>
              </div>
            )}

            {step === 2 && !generating && generated && (
              <div className="cinematic-step1 cinematic-animate-fadeIn">
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                  <h2 className="cinematic-section-title">Review & Edit Script</h2>
                  <p className="cinematic-section-desc">
                    Your three-act case has been generated. Refine each act before publishing.
                  </p>
                </div>

                {error && (
                  <p style={{ color: 'var(--iv-danger)', marginBottom: 16, textAlign: 'center', fontSize: '0.9rem' }}>
                    {error}
                  </p>
                )}

                {summary && (
                  <div className="cinematic-review-box cinematic-section">
                    <div className="cinematic-review-label">Synopsis</div>
                    <div className="cinematic-review-text">{summary}</div>
                  </div>
                )}

                <div className="cinematic-section">
                  <div className="cinematic-scenes-grid">
                    {acts.map((act) => (
                      <div key={act.id} className="cinematic-scene-edit-card">
                        <div className="cinematic-scene-edit-header">
                          <div className="cinematic-scene-label">{act.title}</div>
                        </div>
                        <textarea
                          className="cinematic-textarea"
                          rows={8}
                          value={act.body}
                          onChange={(e) =>
                            setActs((prev) =>
                              prev.map((a) =>
                                a.id === act.id ? { ...a, body: e.target.value } : a
                              )
                            )
                          }
                          placeholder="Edit the generated act here..."
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {!!questions.length && (
                  <div className="cinematic-section">
                    <h3 className="cinematic-subsection-title">Generated Questions & Routes</h3>
                    <div className="cinematic-scenes-grid">
                      {questions.map((q) => (
                        <div key={q.id} className="cinematic-scene-edit-card">
                          <div className="cinematic-scene-edit-header">
                            <div className="cinematic-scene-label">
                              {q.title || `Question ${q.id}`}
                            </div>
                            <div className="cinematic-pill-desc">
                              After scene: {q.after_scene || '—'}
                            </div>
                          </div>
                          <textarea
                            className="cinematic-textarea"
                            rows={6}
                            value={q.text || ''}
                            onChange={(e) =>
                              setQuestions((prev) =>
                                prev.map((item) =>
                                  item.id === q.id ? { ...item, text: e.target.value } : item
                                )
                              )
                            }
                            placeholder="Edit the question stem here..."
                          />
                          {Array.isArray(q.options) && q.options.length > 0 && (
                            <div className="cinematic-review-box" style={{ marginTop: 12 }}>
                              <div className="cinematic-review-label">Options & Routes</div>
                              <ul className="cinematic-review-text">
                                {q.options.map((opt) => (
                                  <li key={opt.id}>
                                    <strong>{opt.id}.</strong> {opt.text}{' '}
                                    {opt.leads_to ? `(→ ${opt.leads_to})` : ''}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(knowledgeGraph.nodes.length > 0 || reasoningGraph.nodes.length > 0) && (
                  <div className="cinematic-section">
                    <h3 className="cinematic-subsection-title">Graphs (Knowledge & Reasoning)</h3>
                    <div className="cinematic-review-box">
                      {knowledgeGraph.nodes.length > 0 && (
                        <div style={{ marginBottom: 16 }}>
                          <div className="cinematic-review-label">Knowledge Graph Nodes</div>
                          <div className="cinematic-review-text">
                            {knowledgeGraph.nodes
                              .map((n) => `${n.id}: ${n.label} [${n.type}]`)
                              .join(' • ')}
                          </div>
                        </div>
                      )}
                      {reasoningGraph.nodes.length > 0 && (
                        <div>
                          <div className="cinematic-review-label">Reasoning Graph Nodes</div>
                          <div className="cinematic-review-text">
                            {reasoningGraph.nodes
                              .map((n) => {
                                const types = Array.isArray(n.reasoning_types)
                                  ? n.reasoning_types.join(', ')
                                  : n.type || '';
                                return `${n.id}: ${n.label} (${types})`;
                              })
                              .join(' • ')}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="cinematic-actions-row">
                  <button type="button" className="cinematic-btn-back" onClick={() => setStep(1)}>
                    Back
                  </button>
                  <button
                    type="button"
                    className="cinematic-btn-generate"
                    onClick={() => setStep(3)}
                    disabled={!acts.length}
                  >
                    <IconSparkles className="cinematic-sparkles" size={24} />
                    Continue
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="cinematic-step1 cinematic-animate-fadeIn">
                <div className="cinematic-ready-header">
                  <IconCheck className="cinematic-check" size={48} />
                  <div>
                    <h2 className="cinematic-ready-title">Experience Ready</h2>
                    <p style={{ color: 'var(--iv-muted)', marginTop: 8, fontSize: '0.875rem' }}>
                      {gameData.vision.substring(0, 100)}...
                    </p>
                  </div>
                </div>

                <div className="cinematic-ready-preview cinematic-section">
                  <div style={{ textAlign: 'center' }}>
                    <IconPlay className="cinematic-play" size={96} />
                    <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>Preview Experience</div>
                    <div style={{ color: 'var(--iv-muted)', marginTop: 8, fontSize: '0.875rem' }}>
                      15-20 minute gameplay
                    </div>
                  </div>
                </div>

                <div className="cinematic-scenes-grid cinematic-section">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <div key={num}>
                      <div className="cinematic-scene-card">
                        <IconCamera className="cinematic-camera" size={32} />
                      </div>
                      <div className="cinematic-scene-label">Scene {num}</div>
                    </div>
                  ))}
                </div>

                <div className="cinematic-stats-box cinematic-section">
                  <h4>Generated Content:</h4>
                  <div className="cinematic-stats-grid">
                    <div>
                      <div className="cinematic-stat-num">5</div>
                      <div className="cinematic-stat-label">Cinematic Scenes</div>
                    </div>
                    <div>
                      <div className="cinematic-stat-num">3</div>
                      <div className="cinematic-stat-label">Critical Questions</div>
                    </div>
                    <div>
                      <div className="cinematic-stat-num">18m</div>
                      <div className="cinematic-stat-label">Average Duration</div>
                    </div>
                  </div>
                </div>

                <div className="cinematic-actions-row">
                  <button
                    type="button"
                    className="cinematic-btn-back"
                    onClick={() => {
                      setStep(1);
                      setGameData({ vision: '', subject: '', difficulty: '' });
                      setGenerated(false);
                    }}
                  >
                    Create Another
                  </button>
                  <button type="button" className="cinematic-publish-btn">
                    Publish to Website
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <AppBarBottom />
    </div>
  );
};

export default StudioPage;
